import React, { useEffect, useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { StratRow, ExtremeCard } from '../../services/analyticsService';
import { getAnalytics, V2AccountAnalytics } from '../../services/optionsTraderV2Service';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';
import { canAccessAutoTrader } from '../../lib/entitlements';

const fmtMoney = (n: number | null | undefined) => {
  if (n == null || !isFinite(n)) return '–';
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}$${s}`;
};
const fmtPct = (n: number | null | undefined) => n == null ? '–' : `${Math.round(n)}%`;
const fmtInt = (n: number | null | undefined) => n == null ? '–' : String(Math.round(n));
const fmtDays = (n: number | null | undefined) => n == null ? '–' : `${n}d`;

const pnlColor = (n: number | null | undefined) =>
  n == null ? '#9ca3af' : n > 0 ? '#4ade80' : n < 0 ? '#f87171' : '#9ca3af';

const HeaderCard: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color = '#f9fafb' }) => (
  <div style={{ background: '#1f2937', borderRadius: 10, padding: '16px 20px', flex: '1 1 160px', minWidth: 140 }}>
    <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{label}</div>
    <div style={{ color, fontSize: 22, fontWeight: 700 }}>{value}</div>
    {sub && <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>{sub}</div>}
  </div>
);

const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#9ca3af', fontSize: 11, fontWeight: 600, borderBottom: '1px solid #374151', textTransform: 'uppercase', letterSpacing: '.03em' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13, color: '#e5e7eb', borderBottom: '1px solid #1f2937' };

const BreakdownTable: React.FC<{ title: string; rows: [string, StratRow][]; keyLabel: string }> = ({ title, rows, keyLabel }) => {
  const sorted = [...rows].sort((a, b) => (b[1].totalPnl || 0) - (a[1].totalPnl || 0));
  return (
    <div style={{ background: '#111827', borderRadius: 10, padding: 16 }}>
      <div style={{ color: '#f9fafb', fontSize: 15, fontWeight: 600, marginBottom: 12 }}>{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>{keyLabel}</th>
              <th style={{ ...th, textAlign: 'right' }}>Trades</th>
              <th style={{ ...th, textAlign: 'right' }}>Win %</th>
              <th style={{ ...th, textAlign: 'right' }}>Avg P&amp;L</th>
              <th style={{ ...th, textAlign: 'right' }}>Total P&amp;L</th>
              <th style={{ ...th, textAlign: 'right' }}>Avg Hold</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td style={{ ...td, color: '#6b7280' }} colSpan={6}>No trades in window</td></tr>
            )}
            {sorted.map(([k, r]) => (
              <tr key={k}>
                <td style={{ ...td, fontWeight: 600 }}>{k.replace(/_/g, ' ')}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmtInt(r.trades)}</td>
                <td style={{ ...td, textAlign: 'right', color: (r.winRate || 0) >= 60 ? '#4ade80' : (r.winRate || 0) < 40 ? '#f87171' : '#e5e7eb' }}>{fmtPct(r.winRate)}</td>
                <td style={{ ...td, textAlign: 'right', color: pnlColor(r.avgPnl) }}>{fmtMoney(r.avgPnl)}</td>
                <td style={{ ...td, textAlign: 'right', color: pnlColor(r.totalPnl), fontWeight: 600 }}>{fmtMoney(r.totalPnl)}</td>
                <td style={{ ...td, textAlign: 'right', color: '#9ca3af' }}>{fmtDays(r.avgHoldDays)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ContextRow: React.FC<{ label: string; ctx: any }> = ({ label, ctx }) => (
  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#9ca3af' }}>
    <span style={{ minWidth: 48, color: '#6b7280' }}>{label}</span>
    {ctx?.underlyingSpot != null && <span>spot <span style={{ color: '#e5e7eb' }}>{ctx.underlyingSpot.toLocaleString()}</span></span>}
    {ctx?.vix != null && <span>VIX <span style={{ color: '#e5e7eb' }}>{ctx.vix}</span></span>}
    {ctx?.ivRank != null && <span>IVR <span style={{ color: '#e5e7eb' }}>{ctx.ivRank}</span></span>}
    {ctx?.regime?.tag && <span style={{ color: '#e5e7eb' }}>{String(ctx.regime.tag).replace(/_/g, ' ').toLowerCase()}</span>}
  </div>
);

const ExtremeCardView: React.FC<{ title: string; card: ExtremeCard | null }> = ({ title, card }) => {
  if (!card) return null;
  const strikes = card.sellStrike == null ? '' : card.buyStrike != null ? `${card.sellStrike}/${card.buyStrike}${card.optionType || ''}` : `${card.sellStrike}${card.optionType || ''}`;
  const isWin = card.netPnl > 0;
  return (
    <div style={{ background: '#111827', borderRadius: 10, padding: 16, borderLeft: `4px solid ${isWin ? '#4ade80' : '#f87171'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ color: '#9ca3af', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>{title}</div>
        <div style={{ color: pnlColor(card.netPnl), fontSize: 20, fontWeight: 700 }}>{fmtMoney(card.netPnl)}</div>
      </div>
      <div style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600 }}>{card.underlying} {strikes} {card.strategy?.replace(/_/g, ' ')}</div>
      <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 10 }}>
        {card.expiration} · held {card.holdDays ?? '–'}d · opened {card.openedAt && new Date(card.openedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
      </div>
      {card.context?.open && <ContextRow label="Open" ctx={card.context.open} />}
      {card.context?.close && <ContextRow label="Close" ctx={card.context.close} />}
      {!card.context && <div style={{ color: '#6b7280', fontSize: 11, fontStyle: 'italic' }}>(market context unavailable for this date)</div>}
    </div>
  );
};

// TT Options Trader v2 (paper) -- Analytics. A copy of v1 OptionsAnalytics.tsx fed by
// GET /v2/analytics (one request; computed on request from v2 paper trades).

const OptionsAnalyticsV2: React.FC = () => {
  const { user, token } = useAuth();
  const [accounts, setAccounts] = useState<V2AccountAnalytics[]>([]);
  const [activeAcct, setActiveAcct] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const enabled = canAccessAutoTrader(user, 'tastytrade');

  useEffect(() => {
    if (!token || !enabled) return;
    setLoading(true);
    getAnalytics(token)
      .then(res => {
        setAccounts(res.accounts || []);
        if (res.accounts?.length && !activeAcct) setActiveAcct(res.accounts[0].accountNumber);
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enabled]);

  const active = useMemo(
    () => accounts.find(a => a.accountNumber === activeAcct) || accounts[0] || null,
    [accounts, activeAcct]
  );

  if (!enabled) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', color: '#9ca3af' }}>
          The TT auto-trader is not enabled on your account.
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <h2 style={{ color: '#f9fafb', margin: 0 }}>Trade Analytics v2</h2>
          {active && (
            <div style={{ color: '#6b7280', fontSize: 12 }}>
              Computed {new Date(active.computedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {active.instanceCount} paper trades
            </div>
          )}
        </div>

        {loading && <p style={{ color: '#6b7280' }}>Loading analytics...</p>}
        {err && <p style={{ color: '#f87171' }}>Failed to load: {err}</p>}

        {!loading && accounts.length === 0 && (
          <div style={{ background: '#111827', borderRadius: 10, padding: 24, color: '#9ca3af' }}>
            No paper trades yet. Analytics appear here once your v2 engines place paper trades.
          </div>
        )}

        {accounts.length > 0 && (
          <>
            {/* Account tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #374151' }}>
              {accounts.map(a => (
                <button key={a.accountNumber}
                  onClick={() => setActiveAcct(a.accountNumber)}
                  style={{
                    background: 'transparent', border: 'none',
                    color: a.accountNumber === activeAcct ? '#4ade80' : '#9ca3af',
                    fontSize: 14, fontWeight: 600, padding: '10px 14px', cursor: 'pointer',
                    borderBottom: a.accountNumber === activeAcct ? '2px solid #4ade80' : '2px solid transparent',
                    marginBottom: -1,
                  }}>
                  Account {a.accountNumber}
                </button>
              ))}
            </div>

            {active && (
              <>
                {/* v2: these P&L numbers come from paper trades only. */}
                <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#94a3b8', fontSize: 12 }}>
                  P&amp;L numbers below are <strong style={{ color: '#fbbf24' }}>paper trades</strong> from your v2 engines ({active.instanceCount} trades on this account). No real orders were placed. The balance tiles show your live TastyTrade account.
                </div>

                {/* P&L header cards */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                  <HeaderCard label="YTD Net P&L"  value={fmtMoney(active.header.ytdNet)}       color={pnlColor(active.header.ytdNet)}       sub={`${active.header.closedThisYearCount} closed YTD`} />
                  <HeaderCard label="Realized YTD" value={fmtMoney(active.header.realizedYtd)}  color={pnlColor(active.header.realizedYtd)} />
                  <HeaderCard label="Unrealized"   value={fmtMoney(active.header.unrealized)}    color={pnlColor(active.header.unrealized)}   sub={`${active.header.openInstances} open`} />
                  <HeaderCard label="Realized (all time)" value={fmtMoney(active.header.realizedAllTime)} color={pnlColor(active.header.realizedAllTime)} sub={`${active.header.closedInstances} total closed`} />
                </div>

                {/* Account balance cards -- populated from tt-account-snapshots-prod */}
                {active.balance ? (
                  <>
                  <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Live account (TastyTrade)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                    <HeaderCard label="Net Liq Value" value={fmtMoney(active.balance.netLiq)} sub={active.balance.snapshotAt ? `as of ${new Date(active.balance.snapshotAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : undefined} />
                    <HeaderCard label="Cash Available" value={fmtMoney(active.balance.cashBalance)} />
                    <HeaderCard label="Options BP" value={fmtMoney(active.balance.derivativeBP)} sub="available" />
                    <HeaderCard label="Stock BP" value={fmtMoney(active.balance.equityBP)} sub="available" />
                    <HeaderCard label="BP In Use" value={fmtMoney(active.balance.maintenanceReq)} sub={active.balance.netLiq > 0 ? `${Math.round((active.balance.maintenanceReq / active.balance.netLiq) * 100)}% of NL` : undefined} color="#fbbf24" />
                    <HeaderCard label="Open Positions" value={String(active.balance.openPositionsCount)} sub="live in TT" />
                  </div>
                  </>
                ) : (
                  <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 14px', marginBottom: 20, color: '#6b7280', fontSize: 12 }}>
                    Live account balance (net liq / BP / cash) not available -- no TT snapshot found for this account yet.
                  </div>
                )}

                {/* Cumulative P&L chart */}
                {(active.cumulativePnlYtd || []).length > 0 && (
                  <div style={{ background: '#111827', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                    <div style={{ color: '#f9fafb', fontSize: 15, fontWeight: 600, marginBottom: 12 }}>YTD Cumulative Realized P&L</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={active.cumulativePnlYtd} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="pnlgrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4ade80" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#4ade80" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                        <ReferenceLine y={0} stroke="#4b5563" />
                        <Tooltip
                          contentStyle={{ background: '#1f2937', border: '1px solid #374151' }}
                          formatter={(v: any) => fmtMoney(Number(v))}
                        />
                        <Area type="monotone" dataKey="cumulative" stroke="#4ade80" fill="url(#pnlgrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Extremes */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 20 }}>
                  <ExtremeCardView title="Biggest Win YTD"  card={active.extremes?.biggestWin ?? null} />
                  <ExtremeCardView title="Biggest Loss YTD" card={active.extremes?.biggestLoss ?? null} />
                </div>

                {/* Breakdown tables */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>
                  <BreakdownTable title="By Strategy" rows={Object.entries(active.byStrategy || {})} keyLabel="Strategy" />
                  <BreakdownTable title="By Symbol"   rows={Object.entries(active.bySymbol || {})}   keyLabel="Symbol" />
                </div>
                <div style={{ marginTop: 12 }}>
                  <BreakdownTable title="By DTE at Entry" rows={Object.entries(active.byDte || {})} keyLabel="DTE bucket" />
                </div>
              </>
            )}
          </>
        )}
        <RiskDisclaimer />
      </div>
    </div>
  );
};

export default OptionsAnalyticsV2;
